import React, { useState } from 'react'
import styled from 'styled-components'
import { X, Users, Play } from 'lucide-react'
import NicknameEntryModal from './NicknameEntryModal'

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px 30px;
  width: 90%;
  max-width: 500px;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
`

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
`

const RoomCodeSection = styled.div`
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  color: white;
  margin-bottom: 20px;
  margin-top: 20px;
`

const RoomCodeTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
`

const RoomCodeDescription = styled.p`
  font-size: 14px;
  margin: 0 0 20px 0;
  opacity: 0.9;
`

const RoomCodeInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`

const RoomCodeInput = styled.input`
  width: 300px;
  max-width: 100%;
  padding: 15px 20px;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  text-align: center;
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
  
  &::placeholder {
    color: #999;
  }
`

const JoinButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  padding: 12px 15px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: white;
    color: #f5576c;
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    border-color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

export default function RoomEntryModal({ isOpen, onClose }) {
  const [roomCode, setRoomCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showNicknameModal, setShowNicknameModal] = useState(false)

  const handleJoinRoom = async () => {
    if (!roomCode.trim()) return

    setIsSubmitting(true)
    
    try {
      // 임시 처리 - 실제로는 서버에 방 코드 검증 요청
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      console.log('방 코드 검증 성공:', roomCode)
      
      // 방 코드 검증 성공 시 닉네임 입력 모달 표시
      setShowNicknameModal(true)
    } catch (error) {
      console.error('방 입장 오류:', error)
      alert('방 입장 중 오류가 발생했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setRoomCode('')
    setShowNicknameModal(false)
    onClose()
  }

  const handleNicknameConfirm = async (nickname, roomCode) => {
    try {
      // 실제로는 서버에 닉네임과 방 코드로 게임 참가 요청
      console.log('게임 참가:', { nickname, roomCode })
      
      // 닉네임 모달에서 네비게이션이 처리되므로 여기서는 모달만 닫기
      setShowNicknameModal(false)
      handleClose()
    } catch (error) {
      console.error('게임 참가 오류:', error)
      alert('게임 참가 중 오류가 발생했습니다.')
    }
  }

  const handleNicknameModalClose = () => {
    setShowNicknameModal(false)
  }

  if (!isOpen) return null

  return (
    <>
      <ModalBackdrop onClick={handleClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={handleClose}>
            <X size={20} />
          </CloseButton>
          
          <RoomCodeSection>
            <RoomCodeTitle>방 번호로 참가하기</RoomCodeTitle>
            <RoomCodeDescription>
              선생님이 공유한 방 코드를 입력하여 게임에 참가하세요
            </RoomCodeDescription>
            <RoomCodeInputContainer>
              <RoomCodeInput
                type="text"
                placeholder="방 코드 입력"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value)}
                autoFocus
              />
              <JoinButton onClick={handleJoinRoom} disabled={!roomCode.trim() || isSubmitting}>
                <Play size={16} />
                {isSubmitting ? '참가 중...' : '참가하기'}
              </JoinButton>
            </RoomCodeInputContainer>
          </RoomCodeSection>
        </ModalContent>
      </ModalBackdrop>
      
      <NicknameEntryModal
        isOpen={showNicknameModal}
        onClose={handleNicknameModalClose}
        onConfirm={handleNicknameConfirm}
        roomCode={roomCode}
      />
    </>
  )
}